import { useState, useEffect, useId } from 'react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';
import { Upload, X } from 'lucide-react';
import './ImageUpload.css';

/** Must stay in sync with max video size in storage.rules */
const MAX_VIDEO_BYTES = 100 * 1024 * 1024;

function isMp4File(file) {
  const nameOk = file.name.toLowerCase().endsWith('.mp4');
  if (!nameOk) return false;
  if (file.type && file.type !== 'video/mp4') return false;
  return true;
}

function VideoUpload({ onUploadComplete, currentVideoUrl, folder = 'hero-videos' }) {
  const inputId = useId();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(currentVideoUrl || '');

  useEffect(() => {
    setPreview(currentVideoUrl || '');
  }, [currentVideoUrl]);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!isMp4File(file)) {
      setError('Please select an MP4 file');
      return;
    }

    if (file.size > MAX_VIDEO_BYTES) {
      setError(`Video must be ${MAX_VIDEO_BYTES / (1024 * 1024)}MB or smaller`);
      return;
    }

    setError('');
    setUploading(true);

    try {
      const sanitizedName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const timestamp = Date.now();
      const filename = `${folder}/${timestamp}_${sanitizedName}`;
      const storageRef = ref(storage, filename);

      const metadata = {
        contentType: 'video/mp4',
        customMetadata: {
          uploadedAt: new Date().toISOString(),
          uploadedBy: 'admin',
          originalName: file.name,
        },
      };

      await uploadBytes(storageRef, file, metadata);
      const downloadURL = await getDownloadURL(storageRef);

      setPreview(downloadURL);
      onUploadComplete(downloadURL);
      setUploading(false);
    } catch (err) {
      console.error('Video upload error:', err);
      if (err.code === 'storage/unauthorized') {
        setError('Permission denied. Sign in as admin and ensure Storage rules allow MP4 uploads.');
      } else if (err.code === 'storage/canceled') {
        setError('Upload canceled.');
      } else if (err.code === 'storage/unknown') {
        setError('Upload failed. Check your connection.');
      } else {
        setError('Upload failed. Please try again.');
      }
      setUploading(false);
    }

    e.target.value = '';
  };

  const clearVideo = () => {
    setPreview('');
    onUploadComplete('');
  };

  return (
    <div className="image-upload">
      {preview ? (
        <div className="image-preview-container">
          <video
            className="image-preview"
            src={preview}
            controls
            muted
            playsInline
            preload="metadata"
          />
          <button
            type="button"
            onClick={clearVideo}
            className="image-remove-btn"
            title="Remove video"
          >
            <X size={20} />
          </button>
        </div>
      ) : (
        <div className="image-upload-area">
          <input
            type="file"
            accept=".mp4,video/mp4"
            onChange={handleFileSelect}
            disabled={uploading}
            className="image-upload-input"
            id={inputId}
          />
          <label htmlFor={inputId} className="image-upload-label">
            <Upload size={32} />
            <span>{uploading ? 'Uploading…' : 'Click to upload MP4'}</span>
            <small>MP4 up to {MAX_VIDEO_BYTES / (1024 * 1024)}MB</small>
          </label>
        </div>
      )}
      {error && <div className="upload-error">{error}</div>}
    </div>
  );
}

export default VideoUpload;
